import React, { useState } from 'react';

// Dot plot: one dot per sales order, x = order date, y = quantity.
// Outliers are ringed; excluded ones fade out so the effect is obvious.
function niceMax(v) {
  if (!v || v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const step = [1, 2, 2.5, 5, 10].find(s => v <= s * base) || 10;
  return step * base;
}

const TICKS = [1, 0.75, 0.5, 0.25, 0];

export default function SiDotChart({ points = [], buckets = [], excluded = {}, median, testid = 'si-dot-chart' }) {
  const [hover, setHover] = useState(null);
  if (!points.length || !buckets.length) {
    return <p className="si-fc-note" data-testid={testid + '-empty'}>No sales orders for this item in the selected period.</p>;
  }
  const W = 760, H = 250, L = 52, R = 14, T = 14, B = 30;
  const t0 = new Date(buckets[0].start + 'T00:00:00').getTime();
  const t1 = new Date(buckets[buckets.length - 1].end + 'T00:00:00').getTime();
  const span = Math.max(1, t1 - t0);
  const yMax = niceMax(Math.max(...points.map(p => p.qty), 1));
  const px = (iso) => L + ((new Date(iso + 'T00:00:00').getTime() - t0) / span) * (W - L - R);
  const py = (q) => H - B - (Math.min(q, yMax) / yMax) * (H - T - B);

  return (
    <div className="si-dot-wrap" data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} className="si-dot-svg" role="img"
        aria-label={`Order quantity over time: ${points.length} orders, peak ${Math.max(...points.map(p => p.qty))} units`}>
        {TICKS.map(t => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={T + (H - T - B) * (1 - t)} y2={T + (H - T - B) * (1 - t)} className="si-dot-grid" />
            <text x={L - 8} y={T + (H - T - B) * (1 - t) + 4} className="si-dot-ytick">{Math.round(yMax * t).toLocaleString()}</text>
          </g>
        ))}
        {median != null && median > 0 && (
          <g>
            <line x1={L} x2={W - R} y1={py(median)} y2={py(median)} className="si-dot-avg" />
            <text x={W - R} y={py(median) - 5} className="si-dot-avgtxt" textAnchor="end">typical order {Math.round(median).toLocaleString()}</text>
          </g>
        )}
        {buckets.map((b, i) => (
          <text key={b.key} x={px(b.start)} y={H - 10} className="si-dot-xtick"
            textAnchor={i === 0 ? 'start' : 'middle'}>{buckets.length > 8 && i % 2 ? '' : b.label}</text>
        ))}
        {points.map((p, i) => {
          const off = excluded[p.key];
          return (
            <circle key={i} cx={px(p.date)} cy={py(p.qty)} r={p.outlier ? 6 : 4}
              className={'si-dot' + (p.outlier ? ' out' : '') + (off ? ' off' : '')}
              data-testid={p.outlier ? 'si-dot-outlier' : 'si-dot'}
              onMouseEnter={() => setHover({ ...p, x: px(p.date), y: py(p.qty) })}
              onMouseLeave={() => setHover(null)} />
          );
        })}
      </svg>
      {hover && (
        <div className="si-dot-tip" data-testid="si-dot-tip"
          style={{ left: `calc(${(hover.x / W) * 100}% - 90px)`, top: `calc(${(hover.y / H) * 100}% - 14px)` }}>
          <b>{hover.qty.toLocaleString()} units</b>
          <span>{hover.date} · {hover.tranid}</span>
          {hover.client && <span>{hover.client}</span>}
          {hover.outlier && <span className="si-dot-tip-out">outlier · moves the average {hover.impact_pct}%</span>}
        </div>
      )}
    </div>
  );
}
